import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Checkbox, Dialog } from "@order-analyzer/ui";
import { api } from "../api.js";
import { RELEASE_NOTES, renderInline } from "../lib/changelog.js";

interface WhatsNewStatus {
  shouldShow: boolean;
  version: string;
}

/**
 * "Neu in dieser Version"-Dialog (App-Konsistenz-Audit gegen TranscriptWebapp, Thema 3): erscheint
 * automatisch nach einem Versionsupdate, ist jederzeit ohne Bestätigung schließbar (natives
 * <dialog>-Element, siehe packages/ui/src/Dialog.tsx - X, Escape und Klick daneben schließen es
 * bereits) und teilt sich die Inhalte bewusst mit dem manuell aufrufbaren ReleaseNotesDialog
 * (dieselbe CHANGELOG.md-Quelle, siehe lib/changelog.ts) statt einer zweiten Datenhaltung.
 */
export function WhatsNewDialog() {
  const [open, setOpen] = React.useState(false);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  const statusQuery = useQuery({
    queryKey: ["whats-new-status"],
    queryFn: () => api.get<WhatsNewStatus>("/users/me/whats-new"),
    staleTime: Infinity,
  });

  React.useEffect(() => {
    if (statusQuery.data?.shouldShow) setOpen(true);
  }, [statusQuery.data?.shouldShow]);

  const updateMutation = useMutation({
    mutationFn: (action: "seen" | "disable") => api.put("/users/me/whats-new", { action }),
  });

  function handleClose() {
    // Unabhängig davon, wie der Dialog geschlossen wird (X-Button, Escape, Klick daneben) - alle
    // Wege laufen hier durch handleClose(), damit die Version in jedem Fall als gesehen vermerkt
    // wird und der Dialog nicht bei jedem Login erneut erscheint.
    updateMutation.mutate(dontShowAgain ? "disable" : "seen");
    setOpen(false);
  }

  const entry = statusQuery.data ? RELEASE_NOTES.find((r) => r.version === statusQuery.data!.version) : undefined;

  return (
    <Dialog open={open} onClose={handleClose} title="Neu in dieser Version" description={statusQuery.data ? `Version ${statusQuery.data.version}` : undefined} className="max-w-lg">
      <div className="flex flex-col gap-4">
        {entry ? (
          <div className="flex flex-col gap-3">
            {entry.summary && <p className="text-sm text-slate-600 dark:text-slate-300">{entry.summary}</p>}
            {entry.sections.map((section) => (
              <div key={section.heading} className="flex flex-col gap-1">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{section.heading}</p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
                  {section.items.map((item, i) => (
                    <li key={i}>{renderInline(item)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          // Kontrolliert, kein Fehler: fehlt ein passender CHANGELOG-Eintrag zur aktuellen Version
          // (sollte praktisch nicht vorkommen, siehe docs/commands.md "check:release"), zeigt der
          // Dialog nur diesen Standardtext.
          <p className="text-sm text-slate-600 dark:text-slate-300">Diese Version enthält kleinere Verbesserungen und Fehlerbehebungen.</p>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Checkbox checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
          In Zukunft nicht mehr anzeigen
        </label>
      </div>
    </Dialog>
  );
}
